/**
 * @file index.tsx
 *
 * @fileoverview Checkbox component. Renders a checkbox together with a label.
 */

import { cva, VariantProps } from "class-variance-authority";
import classnames from "classnames";
import React from "react";
import styles from "./checkbox.module.scss";

const checkbox = cva([styles.checkboxWrapper], {
  variants: {
    checkboxStyle: {
      primary: styles.primary,
      secondary: styles.secondary,
    },
  },
});

const checkboxLabel = cva([styles.checkboxLabel], {
  variants: {
    striked: {
      true: styles.striked,
    },
  },
  defaultVariants: {
    striked: false,
  },
});

export interface CheckboxProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof checkbox>,
    VariantProps<typeof checkboxLabel> {
  /**
   * The label for the checkbox, will show up next to the input.
   *
   * @default ""
   */
  label?: string | React.ReactNode;
  /**
   * Marks if a field is in an error state.
   *
   * @default false
   */
  error?: boolean;
}

/**
 * A checkbox is an input of type checkbox.
 * Will have a label next to it and styling will come from the theme CSS variables.
 * The component inherits props from the "InputHTMLAttributes" so any property for a checkbox can be used with this component.
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      id,
      checkboxStyle,
      label = "",
      onChange,
      error = false,
      required = false,
      disabled = false,
      striked = false,
      ...props
    },
    ref
  ) => {
    return (
      <div className={classnames("jcl-checkbox", checkbox({ checkboxStyle }))}>
        <input
          className={styles.checkboxInput}
          type="checkbox"
          id={id}
          disabled={disabled}
          onChange={onChange}
          ref={ref}
          {...props}
        />
        <label
          className={checkboxLabel({ striked })}
          style={{
            opacity: disabled ? 0.5 : 1,
            color: error ? `var(--color-danger)` : `inherit`,
          }}
          htmlFor={id}
        >
          <div>{label}</div>
          <>
            {required && <span className={styles.checkboxRequiredMark}>*</span>}
          </>
        </label>
      </div>
    );
  }
);
export default Checkbox;
